import { Controller } from "@hotwired/stimulus";

// TV kiosk driver (ADR-0067, #2064). An always-on screen has no remote in hand
// and no one to click "next" — so this controller drives the SHARED airdrop
// report viewer (airdrop_report_viewer_controller.js) into an unattended kiosk
// loop, and keeps the screen current WITHOUT a full-page reload (no white flash):
//
//   1. The viewer self-enters slideshow/present mode on connect via its kiosk
//      value, so we don't trigger it ourselves — we just arm the auto-advance
//      timer against the live viewer instance.
//   2. Auto-advance through the slides on a timer, wrapping back to the first
//      when the last is reached, so the deck plays as a continuous loop.
//   3. RELAXED POLL of the token-authed /tv/status. When the beacon's
//      content_version changes (staff re-points the deck in WIN, or hits
//      "Refresh now"), we reload ONLY the <turbo-frame id="tv_deck"> in place —
//      Turbo re-fetches TvController#deck and swaps the deck with no white flash.
//      This controller wraps OUTSIDE the frame, so it survives the swap and keeps
//      polling.
//   3b. The same poll also reads `livery_key` (ADR-0149). A seasonal livery starting
//      or ending at local midnight changes no column, so content_version never
//      moves; when livery_key differs, we point the <turbo-frame id="tv_livery">
//      overlay at /tv/livery and Turbo swaps it in place. The deck is untouched.
//   4. A LONG self-heal full-page reload as a fallback, in case the frame or poll
//      ever wedges.
//
// It NEVER renders slides itself; it only steers the viewer that already owns
// rendering, and watches a version number to know when to swap the frame.
export default class extends Controller {
  static values = {
    // Seconds each slide is shown before advancing.
    advance: { type: Number, default: 12 },
    // Seconds between /tv/status polls (re-point / refresh detection). Relaxed.
    pollInterval: { type: Number, default: 60 },
    // Seconds between self-heal full-page reloads (fallback only). Default 40 min.
    reload: { type: Number, default: 2400 },
    // The device token, threaded so the poll authenticates as this beacon.
    token: String,
    // The content_version seen at page load. The poll swaps the frame when the
    // server reports a higher value.
    contentVersion: { type: Number, default: 0 },
    // The livery_key seen at page load ("" when no livery is showing). The poll
    // refreshes the overlay frame when the server reports a different one.
    liveryKey: { type: String, default: "" }
  };

  static FRAME_ID = "tv_deck";
  static LIVERY_FRAME_ID = "tv_livery";

  connect() {
    this.lastVersion = this.contentVersionValue;
    this.lastLiveryKey = this.liveryKeyValue;

    // Arm the auto-advance loop against the currently-mounted viewer. The viewer
    // self-enters slideshow on its own connect (kiosk value), so we only need to
    // point our timer at it — a microtask defer lets its connect() finish first.
    queueMicrotask(() => this.armAdvance());

    // Re-arm the advance loop after each in-place frame swap: the swapped-in
    // <turbo-frame> carries a FRESH airdrop-report-viewer instance, so the timer
    // must reference the new controller, not the destroyed one.
    this.boundFrameLoad = (event) => this.onFrameLoad(event);
    this.element.addEventListener("turbo:frame-load", this.boundFrameLoad);

    // Relaxed poll: watch content_version, swap the deck frame in place on change.
    this.pollTimer = window.setInterval(
      () => this.poll(),
      this.pollIntervalValue * 1000
    );

    // Self-heal full reload as a fallback if the frame/poll ever wedges.
    this.reloadTimer = window.setTimeout(
      () => window.location.reload(),
      this.reloadValue * 1000
    );
  }

  disconnect() {
    if (this.advanceTimer) window.clearInterval(this.advanceTimer);
    if (this.pollTimer) window.clearInterval(this.pollTimer);
    if (this.reloadTimer) window.clearTimeout(this.reloadTimer);
    if (this.boundFrameLoad) {
      this.element.removeEventListener("turbo:frame-load", this.boundFrameLoad);
    }
  }

  // ---- Version poll / in-place swap --------------------------------------

  async poll() {
    try {
      const url = `/tv/status?token=${encodeURIComponent(this.tokenValue)}`;
      const response = await fetch(url, { headers: { Accept: "application/json" } });
      if (!response.ok) return;

      const data = await response.json();
      this.refreshLiveryIfChanged(data.livery_key);

      const version = Number(data.content_version);
      if (!Number.isFinite(version)) return;

      // Only act on an actual increase: a fresh content_version means the deck
      // was re-pointed or explicitly refreshed.
      if (version !== this.lastVersion) {
        this.lastVersion = version;
        this.reloadFrame();
      }
    } catch {
      // A transient network blip must never crash the kiosk; the next poll (and
      // ultimately the self-heal reload) recovers.
    }
  }

  // Point the overlay frame at /tv/livery when the active livery changed. The key is
  // part of the URL, so the src always differs and Turbo always loads it. A
  // missing livery_key (an older server) is ignored.
  refreshLiveryIfChanged(liveryKey) {
    if (typeof liveryKey !== "string" || liveryKey === this.lastLiveryKey) return;

    this.lastLiveryKey = liveryKey;
    const frame = document.getElementById(this.constructor.LIVERY_FRAME_ID);
    if (!frame) return;

    const token = encodeURIComponent(this.tokenValue);
    frame.src = `/tv/livery?token=${token}&key=${encodeURIComponent(liveryKey)}`;
  }

  // Reload ONLY the deck frame. Turbo re-fetches TvController#deck and swaps the
  // frame contents in place — no full-page reload, no white flash. The swapped-in
  // viewer self-enters slideshow on connect; turbo:frame-load re-arms our timer.
  reloadFrame() {
    const frame = document.getElementById(this.constructor.FRAME_ID);
    if (frame && typeof frame.reload === "function") frame.reload();
  }

  // After a frame swap, the old viewer (and its slideshow) are gone and a new one
  // has connected. Re-resolve it and re-arm the advance timer against it.
  onFrameLoad(event) {
    if (event.target.id !== this.constructor.FRAME_ID) return;
    this.armAdvance();
  }

  // ---- Auto-advance loop -------------------------------------------------

  // Re-resolve the live viewer and arm the auto-advance timer against it. Safe to
  // call repeatedly: it clears any prior timer first. The viewer enters slideshow
  // itself on connect — we only enter it here as a belt-and-braces guard if it
  // somehow hasn't (the !presenting check prevents double-entry).
  armAdvance() {
    this.viewer = this.findViewer();
    if (this.advanceTimer) window.clearInterval(this.advanceTimer);

    const controller = this.viewerController();
    if (!controller) return;

    if (!controller.presenting) controller.enterSlideshow();

    this.advanceTimer = window.setInterval(() => this.advance(), this.advanceValue * 1000);
  }

  advance() {
    const controller = this.viewerController();
    if (!controller) return;

    const slides = controller.visibleSlides();
    if (!slides.length) return;

    // Wrap to the first slide after the last so the deck plays as a loop.
    const next = controller.index >= slides.length - 1 ? 0 : controller.index + 1;
    controller.goTo(next);
  }

  // The viewer host element (carries data-controller="airdrop-report-viewer").
  // It lives INSIDE the swapped frame, but still within this controller's scope.
  findViewer() {
    return this.element.querySelector("[data-controller~='airdrop-report-viewer']");
  }

  // The viewer's Stimulus controller instance, looked up via the application so
  // we can call its public navigation API directly (enterSlideshow / goTo).
  viewerController() {
    if (!this.viewer) return null;
    return this.application.getControllerForElementAndIdentifier(
      this.viewer,
      "airdrop-report-viewer"
    );
  }
}
